import React, { useCallback } from 'react';
import { IPipeline, IPipelineStage } from 'services/DashboardApi/types';
import { theme } from '../../../../../tailwind.config';
import { ErrorBar } from 'ui/NetworkStatusBar';
import { MetricCard, MetricCardTitle } from './MetricCardComponents';
import classnames from 'classnames';
import { MetricsTooltip } from './MetricsTooltip';

interface ISalesFunnelCardProps {
  pipeline: IPipeline;
  isErrorCrmMetrics: boolean;
}

const SALES_FUNNEL_TOOLTIP_ITEMS = [
  { label: 'Discovery', description: 'Awareness of the company and services.' },
  { label: 'Nurture', description: 'Interest in working together.' },
  { label: 'Confirmed', description: 'Negotiations of terms of services and deal accepted.' },
];

const getStagePercentage = (percentage?: number): number => (percentage !== undefined && Number.isFinite(percentage) ? percentage : 0);

const getFunnelPercentageLabel = (percentage?: number): string => getStagePercentage(percentage).toFixed(0);

const shouldDisplayPercentageInsideBar = (percentage?: number): boolean => getStagePercentage(percentage) >= 1;

const formatLeadCount = (count?: number): string => {
  const safeCount = count !== undefined && Number.isFinite(count) ? count : 0;
  return safeCount.toLocaleString();
};

const getFunnelBarWidth = (percentage: number | undefined, maxPipelinePercentage: number): string => {
  const pct = getStagePercentage(percentage);
  if (pct <= 0) return '1px';
  if (maxPipelinePercentage <= 0) return '1px';
  return `${Math.max(0, (pct / maxPipelinePercentage) * 100)}%`;
};

interface ISalesFunnelStageRowProps {
  label: string;
  stage: IPipelineStage | undefined;
  stageColor: string;
  maxPipelinePercentage: number;
}

const SalesFunnelStageRow: React.FC<ISalesFunnelStageRowProps> = ({
  label,
  stage,
  stageColor,
  maxPipelinePercentage,
}) => {
  const className = classnames('h-full rounded-[4px] flex items-center justify-center', {
    'min-w-[24px]': stage?.percentage && stage?.percentage >= 1,
  });

  return (
    <div className="flex gap-[10px] items-center w-full">
      <p className="m-0 font-pt-sans text-[15px] text-black shrink-0 w-[70px]">{label}</p>
      <div className="flex-1 h-[24px] flex items-center gap-[10px]">
        <div
          className={className}
          style={{
            width: getFunnelBarWidth(stage?.percentage, maxPipelinePercentage),
            backgroundColor: stageColor,
          }}
        >
          {shouldDisplayPercentageInsideBar(stage?.percentage) ? (
            <p className="m-0 font-pt-sans text-white text-[15px] leading-[24px] text-center">
              {getFunnelPercentageLabel(stage?.percentage)}%
            </p>
          ) : null}
        </div>
        <p className="m-0 font-pt-sans text-[15px] shrink-0" style={{ color: stageColor }}>
          {formatLeadCount(stage?.count)}
        </p>
      </div>
    </div>
  );
};

export const SalesFunnelCard: React.FC<ISalesFunnelCardProps> = ({ pipeline, isErrorCrmMetrics }) => {
  const discoveryStage = pipeline.stages.find(s => s.stage === 'DISCOVERY');
  const nurtureStage = pipeline.stages.find(s => s.stage === 'NURTURE');
  const confirmedStage = pipeline.stages.find(s => s.stage === 'CONFIRMED');

  const maxPipelinePercentage = Math.max(
    getStagePercentage(discoveryStage?.percentage),
    getStagePercentage(nurtureStage?.percentage),
    getStagePercentage(confirmedStage?.percentage)
  );

  const renderTooltipContent = useCallback(
    () => (
      <MetricsTooltip items={SALES_FUNNEL_TOOLTIP_ITEMS} />
    ),
    []
  );

  return (
    <MetricCard>
      <MetricCardTitle title="Sales Funnel" renderTooltipContent={renderTooltipContent} />

      {isErrorCrmMetrics ? (
        <div className="w-full flex justify-center items-center mt-[30px] mb-[30px]">
          <ErrorBar />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-[20px] items-start justify-center py-[5px] w-full">
            <SalesFunnelStageRow
              label="Discovery"
              stage={discoveryStage}
              stageColor={theme.colors['status-enquiry']}
              maxPipelinePercentage={maxPipelinePercentage}
            />
            <SalesFunnelStageRow
              label="Nurture"
              stage={nurtureStage}
              stageColor={theme.colors['status-requested']}
              maxPipelinePercentage={maxPipelinePercentage}
            />
            <SalesFunnelStageRow
              label="Confirmed"
              stage={confirmedStage}
              stageColor={theme.colors['status-confirmed']}
              maxPipelinePercentage={maxPipelinePercentage}
            />
          </div>
        </>
      )}
    </MetricCard>
  );
};
